if (!customElements.get('main-product-gallery')) {
  customElements.define('main-product-gallery', class MainProductGallery extends HTMLElement {
    async connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.viewer = this.querySelector('.product__media-wrapper-gallery');
      this.track = this.querySelector('.product__thumbnail-track');
      this.buttons = Array.from(this.querySelectorAll('[data-media-target]'));
      this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
      if (!this.viewer) return;
      this.setupDesktopLayout(signal);

      await customElements.whenDefined('swiper-container');
      if (signal.aborted) return;
      const slides = Array.from(this.viewer.querySelectorAll('swiper-slide'));
      this.mediaIds = slides.map(slide => slide.dataset.mediaId);
      const initialSlide = Math.max(0, this.mediaIds.findIndex(id =>
        id.endsWith(`-${this.viewer.dataset.featuredMedia}`)
      ));
      const syncGalleryWidth = swiper => {
        // Keep fractional grid widths so the next slide cannot peek into view.
        swiper.params.width = swiper.el.getBoundingClientRect().width;
      };
      Object.assign(this.viewer, {
        slidesPerView: 1,
        slidesPerGroup: 1,
        initialSlide,
        speed: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300,
        grabCursor: slides.length > 1,
        allowTouchMove: slides.length > 1,
        pagination: false,
        on: {
          beforeInit: syncGalleryWidth,
          beforeResize: syncGalleryWidth,
          slideChange: () => this.onSlideChange()
        }
      });
      this.viewer.initialize();
      this.onSlideChange();

      this.buttons.forEach(button => {
        button.addEventListener('click', () => this.setActiveMedia(button.dataset.mediaTarget), { signal });
      });
      this.dots.forEach(dot => {
        dot.addEventListener('click', () => this.setActiveMedia(dot.dataset.galleryDot), { signal });
      });
      if (!this.track) return;

      this.track.addEventListener('keydown', event => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        this.stepMedia(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal });

      // Both mouse wheels and horizontal trackpads advance the main image.
      // One step per gesture keeps the upcoming images from racing past.
      this.track.addEventListener('wheel', event => {
        if (event.ctrlKey || this.mediaIds.length < 2) return;
        const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
        if (!delta) return;
        event.preventDefault();
        const now = performance.now();
        if (now < this.wheelLockedUntil) return;
        if (now - this.lastWheelAt > 180 || Math.sign(delta) !== Math.sign(this.wheelDelta)) this.wheelDelta = 0;
        const multiplier = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.track.clientWidth : 1;
        this.wheelDelta = (this.wheelDelta || 0) + delta * multiplier;
        this.lastWheelAt = now;
        if (Math.abs(this.wheelDelta) < 30) return;
        this.stepMedia(Math.sign(this.wheelDelta));
        this.wheelDelta = 0;
        this.wheelLockedUntil = now + 350;
      }, { passive: false, signal });

      this.track.addEventListener('pointerdown', event => {
        this.pointerStart = { x: event.clientX, y: event.clientY };
      }, { signal });
      this.track.addEventListener('pointerup', event => {
        if (!this.pointerStart) return;
        const x = this.pointerStart.x - event.clientX;
        const y = this.pointerStart.y - event.clientY;
        this.pointerStart = null;
        if (Math.abs(x) > 35 && Math.abs(x) > Math.abs(y)) {
          this.suppressClick = true;
          this.stepMedia(Math.sign(x));
        }
      }, { signal });
      this.track.addEventListener('pointercancel', () => { this.pointerStart = null; }, { signal });
      this.track.addEventListener('click', event => {
        if (!this.suppressClick) return;
        this.suppressClick = false;
        event.preventDefault();
        event.stopImmediatePropagation();
      }, { capture: true, signal });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.layoutObserver?.disconnect();
      cancelAnimationFrame(this.layoutFrame);
      this.viewer?.swiper?.destroy();
    }

    setupDesktopLayout(signal) {
      const info = this.querySelector('.product__info-wrapper');
      if (!info) return;
      const updateInfoHeight = () => {
        this.style.setProperty('--product-info-height', `${Math.ceil(info.scrollHeight)}px`);
      };
      this.layoutObserver = new ResizeObserver(() => {
        cancelAnimationFrame(this.layoutFrame);
        this.layoutFrame = requestAnimationFrame(updateInfoHeight);
      });
      this.layoutObserver.observe(info);
      window.addEventListener('resize', updateInfoHeight, { signal });
      document.fonts.ready.then(() => {
        if (!signal.aborted) updateInfoHeight();
      });
      updateInfoHeight();
    }

    stepMedia(direction) {
      const index = this.mediaIds.indexOf(this.activeMediaId);
      this.setActiveMedia(this.mediaIds[(index + direction + this.mediaIds.length) % this.mediaIds.length]);
    }

    setActiveMedia(mediaId) {
      const swiper = this.viewer?.swiper;
      if (!swiper) return;
      // Variant selection may have moved its media to the start of the viewer.
      swiper.update();
      const index = Array.from(swiper.slides).findIndex(slide => slide.dataset.mediaId === mediaId);
      if (index < 0) return;
      swiper.slideTo(index);
      this.onSlideChange();
    }

    onSlideChange() {
      const swiper = this.viewer?.swiper;
      if (!swiper) return;
      const mediaId = swiper.slides[swiper.activeIndex]?.dataset.mediaId;
      if (!mediaId) return;
      this.dots.forEach(dot => {
        if (dot.dataset.galleryDot === mediaId) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (mediaId !== this.activeMediaId) {
        this.activeMediaId = mediaId;
        this.showUpcomingThumbnails();
      }
      swiper.slides.forEach((slide, index) => {
        const active = index === swiper.activeIndex;
        slide.inert = !active;
        if (!active) {
          slide.querySelectorAll('video').forEach(video => video.pause());
          slide.querySelectorAll('.js-youtube').forEach(video => video.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*'));
          slide.querySelectorAll('.js-vimeo').forEach(video => video.contentWindow?.postMessage('{"method":"pause"}', '*'));
        }
      });
    }

    showUpcomingThumbnails() {
      if (!this.track) return;
      const focused = document.activeElement;
      const activeIndex = this.mediaIds.indexOf(this.activeMediaId);
      const upcomingIds = this.mediaIds.slice(activeIndex + 1).concat(this.mediaIds.slice(0, activeIndex + 1));
      const upcoming = upcomingIds.map(id => this.buttons.find(button => button.dataset.mediaTarget === id));
      upcoming.forEach((button, index) => {
        button.hidden = button.dataset.mediaTarget === this.activeMediaId;
        button.tabIndex = index < 2 && !button.hidden ? 0 : -1;
      });
      this.track.replaceChildren(...upcoming);
      this.track.scrollLeft = 0;
      if (this.buttons.includes(focused)) {
        (focused.hidden || focused.tabIndex < 0 ? upcoming[0] : focused).focus({ preventScroll: true });
      }
    }
  });
}
